import { forEach, isArray, isNaN, isNil, isNull, range, uniq } from 'lodash';
import { moment } from 'utils';
import { validateAllFileSize } from 'modules/Taps/utils/validations/files';
import {
  isInteger,
  isLatinNumberSpecials,
  onlyDecimalsNumbers,
  taxPeriodPattern,
} from '../inputTypes';
import { SWIFT_MAX, SWIFT_MIN } from './constants';
import { isRuNumberSpecials, isVin } from 'utils/inputTypes';
import { currentMoment, mmoment } from 'utils/moment';
import Big from 'big.js';

const currentYear = new Date().getFullYear();
const allowedYears = range(currentYear - 3, currentYear + 1);
export const requiredDefaultError = 'Не заполнено обязательное поле';
export const requiredFilesError =
  'К обращению должен быть прикреплен файл с копией подтверждающего документа';
export const emailError = 'Поле должно содержать адрес в формате email@domain.com';
export const phoneError = 'Проверьте корректность введеного номера';
export const positiveNumberError = 'Введенное значение должно быть больше 0';
export const nonNegativeNumberError = 'Введенное значение должно быть больше или равно 0';
export const snilsError =
  'Поле не соответствует формату «XXX-XXX-XXX XX», где X- любая десятичная цифра';
export const passportCodeError =
  'Поле не соответствует формату «XXX-XXX», где X- любая десятичная цифра';
export const passportNumberError =
  'Поле не соответствует формату «XX XX XXXXXX», где X - любая десятичная цифра';
export const innLengthError = 'Поле должно содержать 10 или 12 цифр';
export const objectEndDateError = 'У объекта уже установлена такая дата. Укажите другую дату';
export const innError = 'Некорректный ИНН';
export const kppError = 'Некорректный КПП';
export const yearsStringError =
  'Поле должно содержать годы в формате ГГГГ, указанные через запятую';
export const yearsStringValuesError =
  'Поле может содержать только даты за предыдущие три года, а также текущий год. ' +
  'Список не должен содержать повторяющиеся годы';
export const exceptedSymbolsError = 'Поле содержит недопустимые символы';
export const monthInYearError =
  'Поле не соответствует формату  «XX» или «XX/XX», где X- любая десятичная цифра';
export const emailPattern = /^[a-zа-яё0-9._%+-]+@[a-zа-яё0-9.-]+\.[a-zа-яё]{2,6}$/i;
export const phonePattern = /\+7\([0-9]{3}\)\d{3}-\d{2}-\d{2}/;
export const snilsPattern = /\d{3}-\d{3}-\d{3}\s\d{2}/;
export const yearsPattern = /^\d{4}(?:,\d{4})*$/;
export const passportCodePattern = /\d{3}-\d{3}/;
export const passportNumberPattern = /\d{2}\s\d{2}\s\d{6}|\d{10,11}|\d{4}\s\d{6}/;
export const unknownDateError = 'Введенная дата не существует';
const ownershipSharePattern = /^(?:1|(?:1.00)|(?:0(?:,\d+|\.\d+))|(?:\d)\d*\/\d+)$/;
const ownershipShareError = 'Недопустимый формат обозначения доли';
const monthInYearPattern = /^(?:(?:0?[1-9]|1[012])|(?:(?:0?[1-9]|1[012])\/12))$/;
const taxPeriodError = 'Недопустимый формат значения';
const innFLPattern = /([0-9]{1}[1-9]{1}|[1-9]{1}[0-9]{1})[0-9]{10}/;
const innULPattern = /([0-9]{1}[1-9]{1}|[1-9]{1}[0-9]{1})[0-9]{8}/;
export const userNamePattern = /^[а-яА-ЯёЁIV]+[а-яА-ЯёЁ.\-' IV]*$/g;
export const vinError = 'Недопустимые для ввода символы: I, O, Q';

/**
 * Checks that al least one field should be filled
 * @param errorText
 * @param fieldsList
 * @returns {function(*, *)}
 */
export function atLeastOneRequired(errorText = requiredDefaultError, fieldsList) {
  return (value, values) =>
    fieldsList.some((field) => values.getIn(field.split('.'))) ? null : errorText;
}

export function validateOnlyDecimalsNumbers(maxFloatLength, errorText) {
  return (value) => {
    if (!value) {
      return null;
    }
    return onlyDecimalsNumbers(value, maxFloatLength) ? null : errorText;
  };
}

export function filesAreRequired(value) {
  return !isArray(value) || !value.length ? requiredFilesError : null;
}

//only fied arrays ize
export function validateAllTabFilesSize(allFileMaxSize, fileFieldName) {
  return (value, values) => {
    const formContent = values.toJS();
    let tabList = [];
    forEach(formContent, (value) => {
      isArray(value) && tabList.push(...value);
    });
    const files = [];
    tabList
      .filter((tab) => tab[fileFieldName])
      .forEach((tab) => {
        files.push(...tab[fileFieldName]);
      });
    if (validateAllFileSize(allFileMaxSize, files)) {
      return `Суммарный размер файлов не должен превышать ${allFileMaxSize} МБ`;
    }
    return null;
  };
}

export function getNumberRequiredFn(errorText = requiredDefaultError) {
  return (value) => (value !== undefined && value !== null && value !== '' ? null : errorText);
}

export function getBooleanRequiredFn(errorText = requiredDefaultError) {
  return (value) => {
    return value !== undefined && value !== null ? null : errorText;
  };
}

export function getRequiredFn(errorText = requiredDefaultError) {
  return (value) => (value ? null : errorText);
}

export function getRequiredArray(errorText = requiredDefaultError) {
  return (value) => (!isArray(value) || !value.length ? errorText : null);
}

export function isValidDate(date) {
  if (!date) {
    return null;
  }

  date = moment.isMoment(date) ? date : moment.fromServerDatetime(date);

  return date.isValid() ? null : unknownDateError;
}

export function validateDateRange(startDate, endDate) {
  return moment(endDate).isBefore(startDate)
    ? 'Дата начала периода должна быть позже даты его окончания'
    : null;
}

export function validateDateRangeCustom(startDate, endDate, customFormat) {
  return moment(endDate).isBefore(startDate)
    ? `Поле должно содержать дату не ранее ${
        customFormat ? startDate.format(customFormat) : startDate.defaultFormat()
      } `
    : null;
}

export function getValidateMaxDateFn(endDate) {
  return (value) =>
    value && endDate.isBefore(value)
      ? `Поле должно содержать дату не позднее ${endDate.defaultFormat()} `
      : null;
}

export function getValidateMinDate(minDate) {
  return (value) =>
    value && minDate.isAfter(value)
      ? `Поле должно содержать дату не ранее ${minDate.defaultFormat()} `
      : null;
}

export function getValidateObjectEndDateFn(endDate) {
  return (value) => (value && endDate && endDate.isSame(value) ? objectEndDateError : null);
}

//для ввода в ручную
export function validateDateMinMax(minDate, maxDate) {
  return (value) => {
    if (!value) {
      return null;
    }

    return moment(value).isBetween(minDate, maxDate, null, '[]')
      ? null
      : `Поле должно содержать дату не раньше ${minDate.defaultFormat()} и не позднее ${maxDate.defaultFormat()}`;
  };
}

//для ввода в ручную (2)
export function validateDateMin(minDate) {
  return (value) => {
    if (!value) {
      return null;
    }
    return moment(value).isBefore(minDate, 'day')
      ? `Поле должно содержать дату не раньше ${minDate.defaultFormat()}`
      : null;
  };
}

export function validateNumberMax(
  numberMax,
  errorText = `Поле должно принимать значение до ${numberMax}`,
) {
  return (value) => {
    if (!value) {
      return null;
    }
    return +value <= numberMax ? null : errorText;
  };
}

export const validatePercentage = validateNumberMax(100);

export function validateNumberLength(
  length,
  errorText = `Поле должно содержать ${length} цифр`,
  notRequired,
) {
  return (value) => {
    if (!value && notRequired) {
      return null;
    }
    return isInteger(value) && (value + '').length === length ? null : errorText;
  };
}

export function validateNumberSimpleLength(length) {
  const errorText = `Поле должно содержать ${length} цифр`;

  return (value) => {
    if (isInteger(value) && ('' + value).length !== length) {
      return errorText;
    }
  };
}

export function validateMinLength(minLength, errorText) {
  return (value) => {
    if (!value) {
      return null;
    }
    errorText = errorText || `Поле должно содержать не менее ${minLength} символов`;

    return value.length < minLength ? errorText : null;
  };
}

/**
 * Date field fromat DD.MM.YYYY validation, empty value "" is valid
 * If you need exclude empty values use getRequiredFn()
 * @param errorText
 * @returns {function(*=): *}
 */
export function validateDateField(errorText = 'Поле должно содержать дату в формате ДД.ММ.ГГГГ') {
  return (value) => (!value || moment(value, 'YYYY-MM-DD', true).isValid() ? null : errorText); //YYYY-MM-DD - plugin input
}

export function validateDateAndTime(value) {
  const errorText = 'Поле должно содержать дату и время в формате ДД.ММ.ГГГГ ЧЧ:ММ';
  return !value || moment(value, 'YYYY-MM-DD HH:mm:SS', true).isValid() ? null : errorText;
}

export function validateEmail(value = '', isRequired = true) {
  if (!value) {
    return isRequired ? requiredDefaultError : null;
  }

  return value.match(emailPattern) ? null : emailError;
}

export function validatePhone(value = '') {
  if (!value) {
    return requiredDefaultError;
  }

  return value.match(phonePattern) ? null : phoneError;
}

export function validateUnrequiredPhone(value = '') {
  if (!value) {
    return null;
  }

  return value.match(phonePattern) ? null : phoneError;
}

export function validateSnils(value = '') {
  if (!value) {
    return null;
  }

  return value.match(snilsPattern) ? null : snilsError;
}

export function validatePassportCode(value = '') {
  if (!value) {
    return null;
  }
  return value.match(passportCodePattern) ? null : passportCodeError;
}

export function validatePassportNumber(value = '') {
  if (!value) {
    return null;
  }

  return value.match(passportNumberPattern) ? null : passportNumberError;
}

export function validateInnLength(value = '') {
  if (value && value.length !== 10 && value.length !== 12) {
    return innLengthError;
  } else {
    return null;
  }
}

export function validateInn(value = '') {
  if (value && value.length === 10) {
    const isValidInn = checkTenthInnDigit(value);
    return isValidInn ? null : innError;
  } else if (value && value.length === 12) {
    const isValidInn = checkTwelfthInnDigit(value);
    return isValidInn ? null : innError;
  } else {
    return null;
  }
}

export function validateKpp(value = '') {
  if (!value) {
    return null;
  }

  const isValid = value[4] !== '0' || value[5] !== '0';

  return isValid ? null : kppError;
}

export function getValidatePositiveNumberFn(errorText = positiveNumberError) {
  return (value) => {
    if (!value && value !== 0) {
      return null;
    }

    return +value > 0 ? null : errorText;
  };
}

export function getValidateNonNegativeNumberFn(errorText = nonNegativeNumberError) {
  return (value) => {
    if (!value) {
      return null;
    }

    return +value > 0 ? null : errorText;
  };
}

export function validatePositiveNumber(value) {
  return +value > 0 ? null : positiveNumberError;
}

export function validateYearsString(value = '') {
  if (!value) {
    return null;
  }

  return value.match(yearsPattern) ? null : yearsStringError;
}

export function validateCyrillicName(value = '') {
  if (!value) {
    return null;
  }

  const pattern = getNamePattern(value.length);

  return value.match(pattern) ? null : exceptedSymbolsError;
}

export function validateCyrillicMiddleName(value = '') {
  if (!value) {
    return null;
  }

  const pattern = getMiddleNamePattern(value.length);

  return value.match(pattern) ? null : exceptedSymbolsError;
}

function getNamePattern() {
  return /^[А-ЯЁа-яёIV '.,\-)(`]*$/;
}

function getMiddleNamePattern() {
  return /^[А-ЯЁа-яёIV '.,\-)(`]*$/;
}

export function validateYearsStringValues(value = '') {
  if (!value) {
    return null;
  }
  const yearsValues = value.split(',');
  let isValid = true;

  for (const year of yearsValues) {
    if (!~allowedYears.indexOf(+year)) {
      isValid = false;
      break;
    }
  }

  if (isValid) {
    const uniqValues = uniq(yearsValues);
    isValid = uniqValues.length === yearsValues.length;
  }

  return isValid ? null : yearsStringValuesError;
}

export function validateOwnershipShareField(value) {
  if (!value) {
    return null;
  }
  return value.toString().match(ownershipSharePattern) ? null : ownershipShareError;
}

export function validateMonthInYearField(value) {
  if (!value) {
    return null;
  }
  return value.toString().match(monthInYearPattern) ? null : monthInYearError;
}

function checkTwelfthInnDigit(value) {
  const multipliers = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
  return (
    checkInnControlDigit(value.substring(0, value.length - 1), multipliers) &&
    checkInnControlDigit(value, [3, ...multipliers]) &&
    innFLPattern.test(value)
  );
}

function checkTenthInnDigit(value) {
  const multipliers = [2, 4, 10, 3, 5, 9, 4, 6, 8];
  return checkInnControlDigit(value, multipliers) && innULPattern.test(value);
}

function checkInnControlDigit(value, multipliers) {
  const checkDivider = 11;
  const digits = value.split('');
  const controlDigit = +digits.pop();
  const checkSum = digits.reduce((prev, current, index) => {
    return prev + current * multipliers[index];
  }, 0);
  const remainder = checkSum % checkDivider;
  return remainder === controlDigit || (remainder === 10 && controlDigit === 0);
}

export function musntBe0(errorText) {
  return (value) => (value === 0 || value === '0' ? errorText : null);
}

export function notMoreThan1(errorText) {
  return (value) => {
    if (isNil(value) || (!isNaN(+value) && +value <= 1)) {
      return null;
    }
    const [first, last] = value.split('/');
    return +first / +last > 1 ? errorText : null;
  };
}

export function validateTaxPeriod(value) {
  if (isNil(value)) {
    return null;
  }
  const regExpValid = value.toString().match(taxPeriodPattern);
  let sum;
  if (isNaN(+value)) {
    const [firstNum, secondNum] = value.split('/');
    sum = +Big(firstNum || '1').div(secondNum || '1');
  } else {
    sum = +value;
  }
  const inRange = sum > 0 && sum <= 1;
  return regExpValid && inRange ? null : taxPeriodError;
}

export function validateTaxPeriodMonths(value) {
  if (!value) {
    return null;
  }
  return value && value <= 12 ? null : taxPeriodError;
}

export function isCadastralNumber(value) {
  const regex = /^[0-9]{2}:[0-9]{2}:[0-9]{6,7}:[0-9]{1,6}$/;
  return regex.test(value);
}

export function cadastralNumberValidator(value) {
  if (!value) {
    return null;
  }
  if (!isCadastralNumber(value)) {
    return 'Поле не соответствует формату "ХХ:XX:XXXXXXC:XССССС", где X и С - любая десятичная цифра, С - необязательный элемент';
  }
}

export const validateSwiftBic = (value) => {
  if (!value) return null;
  if (value.length < SWIFT_MIN || value.length > SWIFT_MAX) {
    return `Длина должна быть от ${SWIFT_MIN} до ${SWIFT_MAX} символов`;
  }
  return null;
};

export function normalizeUserName(value, prevValue) {
  return !value || value.search(userNamePattern) === 0 ? value : prevValue;
}

export function validateLatinNumberSpecials(value) {
  if (value && !isLatinNumberSpecials(value)) {
    return 'Допускаются только буквы латинского алфавита, цифры, специальные символы';
  }
  return null;
}

export function validateRuNumberSpecials(value) {
  if (value && !isRuNumberSpecials(value)) {
    return 'Допускаются только буквы русского алфавита, цифры, специальные символы';
  }
  return null;
}

export function getRequiredOrNullFn(errorText = requiredDefaultError) {
  return (value) => (value || isNull(value) ? null : errorText);
}

export function validateMaxYearNumber(value) {
  if (value && value.replace(/_/, '').length !== 4) {
    return 'Поле должно содержать год в формате ГГГГ';
  }
  const date = mmoment(value, 'YYYY');
  if (value && value.length === 4 && currentMoment().isBefore(date)) {
    return 'Год выпуска не должен превышать текущий';
  }
  return null;
}

export function vinNumberValidation(value) {
  if (!value) {
    return null;
  }
  return isVin(value) ? null : vinError;
}

export function validateDictIncludes(list) {
  return (value) => {
    if (!list.find((item) => item.value === value)) {
      return requiredDefaultError;
    }
    return null;
  };
}
